import { formatCurrency } from '@shared';
import { AlertTriangle, Wallet } from 'lucide-react';
import { motion } from 'framer-motion';

import { Button } from '@client/components/ui/button';
import { cn } from '@client/lib/utils';

interface InsufficientFundingBannerProps {
  policyPremium: number;
  totalExistingAssetsValue: number;
  onAdjustFunding: () => void;
  className?: string;
}

export function InsufficientFundingBanner({
  policyPremium,
  totalExistingAssetsValue,
  onAdjustFunding,
  className,
}: InsufficientFundingBannerProps) {
  const shortfall = policyPremium - totalExistingAssetsValue;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn('fixed left-0 right-0 top-0 z-50 w-full', className)}
    >
      <div className="rounded-b-xl border-b border-amber-500 bg-amber-50 p-6 shadow-lg">
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-amber-500 ring-4 ring-amber-200">
              <AlertTriangle className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-base font-semibold text-gray-900">Insufficient Funding</p>
              <p className="mt-0.5 text-sm text-gray-700">
                Your selected policies cost {formatCurrency(policyPremium)}, but your accounts only
                have {formatCurrency(totalExistingAssetsValue)} is existing assets value.
                <span className="ml-1 font-semibold text-amber-700">
                  Deficit: {formatCurrency(shortfall)}
                </span>
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={onAdjustFunding}
            className="flex-shrink-0 border-amber-600 bg-white text-amber-700 hover:bg-amber-50 hover:text-amber-800"
          >
            <Wallet className="mr-2 h-4 w-4" />
            Adjust Funding
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
